/**
 * The three date-display jobs used across the app (session/notification
 * timestamps, "since"/"blocked" dates, the profile's prose "Joined …"),
 * consolidated because they were 11 identical or near-identical call sites
 * across 7 files, each one `new Date(iso).toLocaleString()` or
 * `.toLocaleDateString()` typed out again — a future change to any of them
 * (a different locale strategy, say) needed 11 edits found only by grep,
 * with nothing to catch a 12th call site that types the same expression
 * slightly differently and silently drifts from the other 11.
 *
 * Each function here is exactly the behavior every call site already had —
 * same options, `undefined` locale (the viewer's own, via the platform) —
 * collected rather than changed.
 */

/**
 * Locale date + time, e.g. "9/19/2026, 6:42:00 PM" — for a timestamp where
 * both the day and the moment matter (a session's last action, a
 * notification's created time, an invitation's expiry, sign-in/last-active).
 */
export function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString();
}

/**
 * Locale date only, e.g. "9/19/2026" — for a timestamp where only the day
 * matters (a completed game's date, "friends since", "blocked").
 */
export function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString();
}

const LONG_DATE_FORMAT: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: 'long',
  day: 'numeric',
};

/**
 * Long locale date, e.g. "September 19, 2026" — for a prose context (the
 * profile hero's "Joined …") rather than a compact list row.
 */
export function formatLongDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, LONG_DATE_FORMAT);
}
